import { useMemo, useCallback, useEffect, useState } from 'react';

interface ColumnPreferences {
  order: string[];
  visibility: string[];
}

type TableSize = 'small' | 'middle' | 'large';

export interface TablePreferences {
  [tableName: string]: {
    columnPreferences: ColumnPreferences;
    activeFilters: string[];
    size: TableSize;
  };
}

interface CustomerPreferences {
  tables: TablePreferences;
}

interface UserPreferences {
  [userId: string]: {
    [customerId: string]: CustomerPreferences;
  };
}

interface SetTablePreferences {
  columnPreferences?: ColumnPreferences;
  activeFilters?: string[];
  size?: TableSize;
  newSelectedFilters?: string[];
}

interface SetUserAttributesParams {
  dateFrom?: string;
  alreadyViewEvents?: string;
}

export interface UserPreferencesReturn {
  setTablePreferences: (
    tableName: string,
    preferences: SetTablePreferences,
  ) => void;
  getTablePreferences: (tableName: string) =>
    | {
        columnPreferences: ColumnPreferences;
        activeFilters: string[];
        size: TableSize;
        filterSelected?: string[];
      }
    | undefined;
  getUserAttributes: () => any | undefined;
  setUserAttributes: (
    attributes: SetUserAttributesParams,
    defaultLocal?: any,
  ) => void;
  setUserPreferences: (newPreferences: { [key: string]: any }) => void;
}

const useUserPreferences = (
  userId: string,
  customerId: number,
): UserPreferencesReturn => {
  const [preferences, setPreferences] = useState<UserPreferences>();

  useEffect(() => {
    const storageData = localStorage.getItem('userPreferences');
    if (storageData) {
      setPreferences(JSON.parse(storageData));
    }
  }, [setPreferences]);

  const user = useMemo(() => `user_${userId}`, [userId]);
  const customer = useMemo(() => `context_${customerId}`, [customerId]);

  const userPreferences = useMemo(
    () => preferences?.[user],
    [preferences, user],
  );

  const contextPreferences = useMemo(
    () => userPreferences?.[customer],
    [customer, userPreferences],
  );

  const contextTablesPreferences = useMemo(
    () => userPreferences?.[customer]?.tables,
    [customer, userPreferences],
  );

  const getTablePreferences = useCallback(
    (tableName: string) => contextTablesPreferences?.[tableName],
    [contextTablesPreferences],
  );

  const setUserPreferences = useCallback(
    (newPreferences: { [key: string]: any }) =>
      localStorage.setItem(
        'userPreferences',
        JSON.stringify({
          ...preferences,
          [user]: {
            ...userPreferences,
            ...newPreferences,
          },
        }),
      ),
    [preferences, user, userPreferences],
  );

  const setTablePreferences = useCallback(
    (
      tableName: string,
      {
        columnPreferences,
        activeFilters,
        size,
        newSelectedFilters,
      }: SetTablePreferences,
    ) => {
      const tablePreferences = getTablePreferences?.(tableName);
      const newPreferences = {
        ...preferences,
        [user]: {
          ...userPreferences,
          [customer]: {
            ...contextPreferences,
            tables: {
              ...contextTablesPreferences,
              [tableName]: {
                size: size ?? tablePreferences?.size,
                columnPreferences:
                  columnPreferences ?? tablePreferences?.columnPreferences,
                activeFilters: activeFilters ?? tablePreferences?.activeFilters,
                filterSelected: newSelectedFilters,
              },
            },
          },
        },
      };
      localStorage.setItem('userPreferences', JSON.stringify(newPreferences));
      // @ts-ignore
      setPreferences(newPreferences);
    },
    [
      contextPreferences,
      contextTablesPreferences,
      customer,
      getTablePreferences,
      preferences,
      user,
      userPreferences,
    ],
  );

  const getUserAttributes = useCallback(() => {
    return {
      dateFrom: userPreferences?.dateFrom,
      alreadyViewEvents: userPreferences?.alreadyViewEvents,
    };
  }, [userPreferences]);

  const setUserAttributes = useCallback(
    ({ dateFrom, alreadyViewEvents }: any, defaultLocal: any) => {
      const newPreferences = {
        ...preferences,
        [user]: {
          ...(defaultLocal ?? userPreferences),
          dateFrom: dateFrom ?? userPreferences?.dateFrom,
          alreadyViewEvents:
            alreadyViewEvents ?? userPreferences?.alreadyViewEvents,
        },
      };
      localStorage.removeItem('userPreferences');
      localStorage.setItem('userPreferences', JSON.stringify(newPreferences));
      setPreferences(newPreferences);
    },
    [preferences, user, userPreferences],
  );

  return {
    setTablePreferences,
    getTablePreferences,
    getUserAttributes,
    setUserAttributes,
    setUserPreferences,
  };
};

export default useUserPreferences;
